/**
 * Layout – Global application shell
 *
 * Blueprint-compliant:
 *  • 56 px sticky header with breadcrumb, theme toggle, notifications,
 *    profile chip ("Role • Username")
 *  • Collapsible sidebar: 240 px expanded → 72 px collapsed
 *    – manual toggle only
 *    – tooltip on collapsed icons
 *    – grouped navigation with labels
 *  • Content area max-width 1280–1440 px
 *  • Mobile overlay drawer (< lg breakpoint)
 *
 * All existing route + permission logic is preserved; only the presentation
 * layer is updated.
 */

import React, { ReactNode, useState, useEffect, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useAppThemeSettings } from '../contexts/AppThemeContext';
import { useProgramTheme } from '../contexts/ProgramThemeContext';
import { useUserTheme } from '../contexts/UserThemeContext';
import { useProgramRoleMenuPermissions } from '@/modules/programs/hooks/useProgramRoleMenuPermissions';
import { usePatientRegistrationApprovalPendingCount } from '@/modules/programs/hooks/usePatientRegistrationApprovals';
import { usePatient360ModuleAvailability } from '@/modules/programs/hooks/usePatient360ModuleAvailability';
import { MENU_KEYS } from '@/modules/programs/services/programRoleMenuPermissionService';
import type { LegalPageLink } from '@/modules/shared/types/legalPage';
import {
  getProgramWorkspaceKind,
  isConfigurationDashboard,
  resolveDashboardLandingPath,
} from '@/modules/programs/dashboard-governance/DashboardLandingResolver';
import { SUPERADMIN_MENU_GROUPS } from '@/modules/superadmin/constants/navigation';
import { appRoutes, programRoutes } from '../constants/routes';
import { cn } from '@/lib/utils';
import { AppInlineLoader } from './ui/AppLoading';
import { useAppVersion } from '../hooks/useAppVersion';
import { legalPageService } from '../services/legalPageService';
import { programService } from '@/modules/programs/services/programService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  BarChart3,
  FolderKanban,
  ClipboardList,
  UserCog,
  FileText,
  Workflow,
  Bell,
  Shield,
  ShieldCheck,
  Palette,
  Wallet,
  IndianRupee,
  Zap,
  CheckSquare,
  FileLock,
  FileCheck,
  GitBranch,
  Users,
  UserPlus,
  Briefcase,
  Inbox,
  ListChecks,
  MapPin,
  Phone,
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  Sparkles,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { Button, IconGhostButton } from './ui/button';
import { AppIcon } from './ui/AppIcon';
import { PatientQuickViewProvider, usePatientQuickView } from '@/modules/programs/context/PatientQuickViewContext';
import { PatientQuickView } from '@/modules/programs/components/PatientQuickView';
import { useExplain } from '../contexts/ExplainContext';
import { AlertsBell } from './AlertsPanel';
import { useInsights } from '../hooks/useInsights';
import { PageInstructionBanner } from './PageInstructionBanner';
import { PageInstructionRenderProvider } from './PageInstructionRenderContext';
import { useResolvedGlobalPageInstruction } from '../hooks/useResolvedGlobalPageInstruction';
import { resolveWorkspaceKind, getWorkspaceNavConfig, getPatient360NavItems, getPatient360BasePath, type WorkspaceKind as WsKind, type WorkspaceNavItem } from '../constants/workspaceNavigation';
import { WorkspaceSidebar } from './WorkspaceSidebar';
import { SidebarFooter } from './ui/SidebarFooter';
import { useSidebarCollapse } from '../hooks/useSidebarCollapse';
import { ProgramBrandHeader } from './ProgramBrandHeader';
import { WorkspaceNavTabs } from '@/modules/programs/components/workspace/WorkspaceNavTabs';
import { Patient360NavTabs } from '@/modules/programs/components/patient360/Patient360NavTabs';

/* ───────────────────────────────────────────────────────────── */
/*  Constants                                                    */
/* ───────────────────────────────────────────────────────────── */

const EPPREM_LOGO_URL = 'https://epprem.com/api/uploaded_files/6378917665414535811.png';

/* ───────────────────────────────────────────────────────────── */
/*  Navigation types                                             */
/* ───────────────────────────────────────────────────────────── */

interface NavItem {
  name: string;
  href: string | ((programId: string) => string);
  icon: LucideIcon;
  menuKey?: string;
  allowedProgramRoles?: string[];
  badge?: number;
  isWorkflowDriven?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const CANONICAL_PRIVACY_KEY = 'privacy-policy';
const CANONICAL_TERMS_KEY = 'terms-and-conditions';

export function normalizeLegalMenuKey(titleOrKey: string): string {
  const normalized = titleOrKey
    .toLowerCase()
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[_/]+/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (/\bprivacy\s+policy\b/.test(normalized)) {
    return CANONICAL_PRIVACY_KEY;
  }

  if (/\bterms?\s+(and\s+)?conditions?\b/.test(normalized) || /\btermsconditions\b/.test(normalized)) {
    return CANONICAL_TERMS_KEY;
  }

  return normalized.replace(/\s+/g, '-');
}

function selectPreferredLegalLink(existing: LegalPageLink, candidate: LegalPageLink): LegalPageLink {
  const existingPageKey = normalizeLegalMenuKey(existing.pageKey || '');
  const candidatePageKey = normalizeLegalMenuKey(candidate.pageKey || '');
  const existingTitleKey = normalizeLegalMenuKey(existing.pageTitle || '');
  const candidateTitleKey = normalizeLegalMenuKey(candidate.pageTitle || '');

  const existingCanonical = existingPageKey === existingTitleKey;
  const candidateCanonical = candidatePageKey === candidateTitleKey;

  if (candidateCanonical && !existingCanonical) {
    return candidate;
  }

  return existing;
}

function dedupeUserMenuLegalLinks(links: LegalPageLink[]): LegalPageLink[] {
  const deduped = new Map<string, LegalPageLink>();

  for (const link of links) {
    const normalizedPageKey = normalizeLegalMenuKey(link.pageKey || '');
    const normalizedTitleKey = normalizeLegalMenuKey(link.pageTitle || '');
    const dedupeKey =
      normalizedPageKey === CANONICAL_PRIVACY_KEY || normalizedTitleKey === CANONICAL_PRIVACY_KEY
        ? CANONICAL_PRIVACY_KEY
        : normalizedPageKey === CANONICAL_TERMS_KEY || normalizedTitleKey === CANONICAL_TERMS_KEY
          ? CANONICAL_TERMS_KEY
          : normalizedTitleKey || normalizedPageKey;

    if (!dedupeKey) {
      continue;
    }

    const existing = deduped.get(dedupeKey);
    if (!existing) {
      deduped.set(dedupeKey, link);
      continue;
    }

    deduped.set(dedupeKey, selectPreferredLegalLink(existing, link));
  }

  return Array.from(deduped.values());
}

/* ───────────────────────────────────────────────────────────── */
/*  Static navigation definitions                                */
/* ───────────────────────────────────────────────────────────── */

/** Program-level static menu items */
const programStaticItems: { group: string; items: NavItem[] }[] = [
  {
    group: 'Main',
    items: [
      { name: 'Dashboard', href: (pid: string) => programRoutes.dashboard(pid), icon: BarChart3, menuKey: MENU_KEYS.PROGRAM_DASHBOARD },
      { name: 'My Work', href: (pid: string) => programRoutes.myWork(pid), icon: Inbox, menuKey: MENU_KEYS.MY_WORK },
      { name: 'Workspace', href: (pid: string) => programRoutes.workspace(pid), icon: Briefcase, menuKey: MENU_KEYS.WORKSPACE },
      { name: 'Pharma Workspace', href: (pid: string) => programRoutes.workspace(pid), icon: FolderKanban, menuKey: MENU_KEYS.PHARMA_WORKSPACE },
    ],
  },
  {
    group: 'Setup',
    items: [
      { name: 'Roles', href: (pid: string) => programRoutes.roles(pid), icon: UserCog, menuKey: MENU_KEYS.ROLES_CONFIG },
      { name: 'Users', href: (pid: string) => programRoutes.userAssignments(pid), icon: Users, menuKey: MENU_KEYS.USERS_CONFIG },
      { name: 'Forms', href: (pid: string) => programRoutes.forms(pid), icon: FileText, menuKey: MENU_KEYS.FORMS_CONFIG },
      { name: 'Workflow', href: (pid: string) => programRoutes.workflow(pid), icon: Workflow, menuKey: MENU_KEYS.WORKFLOW_CONFIG },
      { name: 'Self-Registration', href: (pid: string) => programRoutes.selfRegistrationSettings(pid), icon: UserPlus, menuKey: MENU_KEYS.ASSISTANCE_SETTINGS },
      { name: 'Permission Matrix', href: (pid: string) => programRoutes.permissionsMatrix(pid), icon: ShieldCheck, allowedProgramRoles: ['Program Admin', 'ProgramAdmin', 'Program Administrator', 'CCM', 'Care Coordinator Manager'] },
    ],
  },
  {
    group: 'Financial',
    items: [
      { name: 'Assistance Settings', href: (pid: string) => programRoutes.assistanceSettings(pid), icon: Wallet, menuKey: MENU_KEYS.ASSISTANCE_SETTINGS },
      { name: 'Financial Plans', href: (pid: string) => programRoutes.plans4Model(pid), icon: IndianRupee, menuKey: MENU_KEYS.FINANCIAL_PLAN },
      { name: 'Cases', href: (pid: string) => programRoutes.workspaceCases(pid), icon: ListChecks, menuKey: MENU_KEYS.ASSISTANCE_CASES },
    ],
  },
  {
    group: 'Engagement',
    items: [
      { name: 'Call Logs', href: (pid: string) => programRoutes.callLogs(pid), icon: Phone, menuKey: MENU_KEYS.CALL_LOGS },
    ],
  },
  {
    group: 'System',
    items: [
      { name: 'Notifications', href: (pid: string) => programRoutes.notifications(pid), icon: Bell, menuKey: MENU_KEYS.NOTIFICATIONS_CONFIG },
      { name: 'Automation Rules', href: (pid: string) => programRoutes.automationRules(pid), icon: Zap, menuKey: MENU_KEYS.AUTOMATION_RULES },
      { name: 'Rule Review', href: (pid: string) => programRoutes.automationRuleReviewQueue(pid), icon: CheckSquare, menuKey: MENU_KEYS.AUTOMATION_RULE_REVIEW },
      { name: 'UI Settings', href: (pid: string) => programRoutes.uiSettings(pid), icon: Palette, menuKey: MENU_KEYS.UI_SETTINGS },
      { name: 'Legal Pages', href: (pid: string) => programRoutes.legalPages(pid), icon: FileLock, menuKey: MENU_KEYS.LEGAL_PAGES },
      { name: 'Audit Report', href: (pid: string) => programRoutes.auditReport(pid), icon: ClipboardList, menuKey: MENU_KEYS.AUDIT_REPORT },
      { name: 'Location Settings', href: (pid: string) => programRoutes.locationSettings(pid), icon: MapPin, menuKey: 'LOCATION_SETTINGS' },
      { name: 'Entity Inheritance', href: (pid: string) => programRoutes.entityInheritance(pid), icon: GitBranch, menuKey: 'ENTITY_INHERITANCE' },
      { name: 'Document Requirements', href: (pid: string) => programRoutes.documentRequirements(pid), icon: FileCheck, menuKey: 'DOCUMENT_REQUIREMENTS' },
    ],
  },
];
/* ───────────────────────────────────────────────────────────── */
/*  Layout component                                             */
/* ───────────────────────────────────────────────────────────── */

interface LayoutProps {
  children?: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  useAppVersion();
  const params = useParams<{ programId?: string }>();
  const appThemeSettings = useAppThemeSettings();
  const { settings: programThemeSettings } = useProgramTheme();
  const { theme, toggleTheme } = useUserTheme();
  const { openExplainPanel } = useExplain();
  const resolvedPageInstruction = useResolvedGlobalPageInstruction();
  // Run insights on every page render so the AlertsBell stays live.
  useInsights();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin';

  // Sidebar state – persisted via localStorage.
  const [collapsed, toggleSidebar] = useSidebarCollapse();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Manual toggles – only for sections NOT currently active (active section always open)
  const [manualOpen, setManualOpen] = useState<Record<string, boolean>>({});

  const isSidebarExpanded = !collapsed;
  const isSidebarIconMode = collapsed;

  // Extract programId from URL
  const programId = params.programId || location.pathname.match(/\/programs\/([^/]+)/)?.[1];

  // Role-based menu permissions with governance info
  const { 
    canAccessMenu, 
    programRoleName, 
    isLoading: isLoadingMenuAccess,
    isConfigurationRole: isConfigRole,
    defaultLandingRoute,
    dashboardKey,
    landingRoute,
  } = useProgramRoleMenuPermissions({
    programId,
    enabled: !!programId && !isSuperAdmin,
  });

  const effectiveProgramRoleName = programRoleName ?? user?.programRoleName;
  const workspaceKind = getProgramWorkspaceKind({
    programId,
    dashboardKey: dashboardKey || user?.dashboardKey,
    landingRoute: landingRoute || user?.landingRoute,
    defaultLandingRoute,
  });
  
  // Governance-aware: Check if this is a configuration role (e.g., Program Admin)
  const isProgramAdminRole = isConfigRole || isConfigurationDashboard({
    programId,
    dashboardKey: dashboardKey || user?.dashboardKey,
    landingRoute: landingRoute || user?.landingRoute,
    defaultLandingRoute,
  });
  
  // Role-scoped workspace users get specialized workspace (hospital, hcp, patient, pharma)
  const isRoleScopedWorkspaceUser = workspaceKind !== 'none' && !isProgramAdminRole;
  
  // Calculate home path based on governance type
  const roleHomePath = !isSuperAdmin && programId
    ? (isRoleScopedWorkspaceUser && workspaceKind === 'program')
      ? programRoutes.operationalDashboard(programId)
      : (isProgramAdminRole)
        ? programRoutes.operationalDashboard(programId)
        : resolveDashboardLandingPath({ 
            programId,
            dashboardKey: dashboardKey || user?.dashboardKey,
            landingRoute: landingRoute || user?.landingRoute,
            defaultLandingRoute,
            fallbackRoute: programRoutes.operationalDashboard(programId),
          })
    : appRoutes.dashboard();

  // Legal page links for footer and user menu
  useQuery({
    queryKey: ['legalPages', 'footer-links', programId],
    queryFn: () => legalPageService.getFooterLinks(programId),
  });
  const { data: userMenuLinksResponse } = useQuery({
    queryKey: ['legalPages', 'user-menu-links', programId],
    queryFn: () => legalPageService.getUserMenuLinks(programId),
  });
  const userMenuLinks = useMemo(() => dedupeUserMenuLegalLinks(userMenuLinksResponse?.data || []), [userMenuLinksResponse?.data]);

  // Fetch program name for breadcrumb context
  const { data: programDataResponse } = useQuery({
    queryKey: ['program', programId, 'name'],
    queryFn: () => programService.getProgramById(programId!),
    enabled: !!programId,
    staleTime: 5 * 60 * 1000,
  });
  const programName = programDataResponse?.data?.programName;

  // ─── Patient Context Detection ─────────────────────────────────────────
  // Extract patientId from route when viewing Patient360 pages.
  // This enables the contextual Patient Workspace submenu.
  const activePatientId = useMemo(() => {
    const match = location.pathname.match(/\/patients\/([^/]+)\/360(\/|$)/);
    return match?.[1] || null;
  }, [location.pathname]);

  const { modules } = usePatient360ModuleAvailability(programId, {
    enabled: Boolean(programId) && Boolean(activePatientId),
  });
  const aePcModule = modules['ae-pc'];
  const callLogsModule = modules['call-logs'];

  const patient360SubmenuItems = useMemo(() => {
    let items = getPatient360NavItems().filter((item) => item.key !== 'financials');

    // Hide until workflow readiness resolves to avoid a visible flash.
    if (aePcModule.isLoading || !aePcModule.isAvailable) {
      items = items.filter((item) => item.key !== 'ae-pc');
    }

    // Hide Call Logs until access/config readiness resolves.
    if (callLogsModule.isLoading || !callLogsModule.isAvailable) {
      items = items.filter((item) => item.key !== 'call-logs');
    }

    return items;
  }, [
    aePcModule.isLoading,
    aePcModule.isAvailable,
    callLogsModule.isLoading,
    callLogsModule.isAvailable,
  ]);

  // Patient Workspace submenu expansion state
  // Auto-expand when patient is selected, allow manual toggle otherwise
  const [patientWorkspaceExpanded, setPatientWorkspaceExpanded] = useState(false);
  
  // Auto-expand Patient Workspace submenu when a patient is selected
  useEffect(() => {
    if (activePatientId) {
      setPatientWorkspaceExpanded(true);
    }
  }, [activePatientId]);

  const routeSection = useMemo<'dashboard' | 'programs' | 'admin'>(() => {
    const p = location.pathname;
    if (p === '/programs' || p.startsWith('/programs/')) return 'programs';
    if (isSuperAdmin && p.startsWith('/superadmin/') && p !== '/superadmin/dashboard') return 'admin';
    return 'dashboard';
  }, [location.pathname, isSuperAdmin]);

  // Reset manual overrides whenever the active section changes (route wins)
  useEffect(() => { setManualOpen({}); }, [routeSection]);

  // Compute final open state: active section always open, others follow manual toggle
  const openSections = {
    dashboard: routeSection === 'dashboard' || !!manualOpen.dashboard,
    programs:  routeSection === 'programs'  || !!manualOpen.programs,
    admin:     routeSection === 'admin'     || !!manualOpen.admin,
  };

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  /* ───── Build grouped program submenu ───── */

  const programNavGroups = useMemo<NavGroup[]>(() => {
    if (!programId) return [];

    // ─────────────────────────────────────────────────────────────
    // GOVERNANCE-AWARE Permission Filter
    // ─────────────────────────────────────────────────────────────
    const canAccess = (item: NavItem): boolean => {
      const menuKey = item.menuKey;
      const normalizedProgramRoleName = (effectiveProgramRoleName ?? '').trim().toLowerCase();
      
      // SuperAdmin: Show all config menus, hide workspace menus
      if (isSuperAdmin) {
        return menuKey !== 'WORKSPACE'
          && menuKey !== 'PHARMA_WORKSPACE'
          && menuKey !== 'HOSPITAL_WORKSPACE'
          && menuKey !== 'HCP_WORKSPACE'
          && menuKey !== 'PATIENT_WORKSPACE'
          && menuKey !== 'ASSISTANCE_CASES';
      }

      if (item.allowedProgramRoles && item.allowedProgramRoles.length > 0) {
        return item.allowedProgramRoles.some((roleName) => roleName.trim().toLowerCase() === normalizedProgramRoleName);
      }

      // No menu key = accessible
      if (!menuKey) return true;
      return canAccessMenu(menuKey);
    };

    const groups: NavGroup[] = [];

    // ─────────────────────────────────────────────────────────────
    // GOVERNANCE: Build menu based on role type
    // ─────────────────────────────────────────────────────────────
    
    // SuperAdmin: Show all configuration menus (no workspace)
    if (isSuperAdmin) {
      // Dashboard
      const dashboardItem: NavItem = { 
        name: 'Dashboard', 
        href: (pid: string) => programRoutes.dashboard(pid), 
        icon: BarChart3, 
        menuKey: MENU_KEYS.PROGRAM_DASHBOARD,
      };
      groups.push({ label: '', items: [dashboardItem] });
      
      // Setup group
      const setupItems: NavItem[] = [
        { name: 'Roles', href: (pid: string) => programRoutes.roles(pid), icon: UserCog, menuKey: MENU_KEYS.ROLES_CONFIG },
        { name: 'Users', href: (pid: string) => programRoutes.userAssignments(pid), icon: Users, menuKey: MENU_KEYS.USERS_CONFIG },
        { name: 'Forms', href: (pid: string) => programRoutes.forms(pid), icon: FileText, menuKey: MENU_KEYS.FORMS_CONFIG },
        { name: 'Workflow', href: (pid: string) => programRoutes.workflow(pid), icon: Workflow, menuKey: MENU_KEYS.WORKFLOW_CONFIG },
        { name: 'Self-Registration', href: (pid: string) => programRoutes.selfRegistrationSettings(pid), icon: UserPlus, menuKey: MENU_KEYS.ASSISTANCE_SETTINGS },
        { name: 'Permission Matrix', href: (pid: string) => programRoutes.permissionsMatrix(pid), icon: ShieldCheck },
      ];
      groups.push({ label: 'Setup', items: setupItems });
      
      // Financial group (excluding Cases which is workspace-related)
      const financialItems: NavItem[] = [
        { name: 'Assistance Settings', href: (pid: string) => programRoutes.assistanceSettings(pid), icon: Wallet, menuKey: MENU_KEYS.ASSISTANCE_SETTINGS },
        { name: 'Financial Plans', href: (pid: string) => programRoutes.plans4Model(pid), icon: IndianRupee, menuKey: MENU_KEYS.FINANCIAL_PLAN },
      ];
      groups.push({ label: 'Financial', items: financialItems });
      
      // System group
      const systemItems: NavItem[] = [
        { name: 'Notifications', href: (pid: string) => programRoutes.notifications(pid), icon: Bell, menuKey: MENU_KEYS.NOTIFICATIONS_CONFIG },
        { name: 'Automation Rules', href: (pid: string) => programRoutes.automationRules(pid), icon: Zap, menuKey: MENU_KEYS.AUTOMATION_RULES },
        { name: 'Rule Review', href: (pid: string) => programRoutes.automationRuleReviewQueue(pid), icon: CheckSquare, menuKey: MENU_KEYS.AUTOMATION_RULE_REVIEW },
        { name: 'UI Settings', href: (pid: string) => programRoutes.uiSettings(pid), icon: Palette, menuKey: MENU_KEYS.UI_SETTINGS },
        { name: 'Legal Pages', href: (pid: string) => programRoutes.legalPages(pid), icon: FileLock, menuKey: MENU_KEYS.LEGAL_PAGES },
        { name: 'Audit Report', href: (pid: string) => programRoutes.auditReport(pid), icon: ClipboardList, menuKey: MENU_KEYS.AUDIT_REPORT },
        { name: 'Document Requirements', href: (pid: string) => programRoutes.documentRequirements(pid), icon: FileCheck, menuKey: 'DOCUMENT_REQUIREMENTS' },
      ];
      groups.push({ label: 'System', items: systemItems });
      
      return groups;
    }

    if (isRoleScopedWorkspaceUser) {
      // Primary navigation: Overview (opt-in) + Patient Workspace (always present)
      const primaryItems: NavItem[] = [];
      if (canAccessMenu(MENU_KEYS.OPERATIONAL_DASHBOARD)) {
        primaryItems.push({
          name: 'Overview',
          href: (pid: string) => programRoutes.operationalDashboard(pid),
          icon: LayoutDashboard,
          menuKey: MENU_KEYS.OPERATIONAL_DASHBOARD,
        });
      }
      primaryItems.push({
        name: 'Patient Workspace',
        href: (pid: string) => programRoutes.workspace(pid),
        icon: Users,
        menuKey: MENU_KEYS.WORKSPACE,
      });
      groups.push({ label: '', items: primaryItems });

      // My Work (approval queue) — opt-in per program via MY_WORK, separate
      // from the WORKSPACE_APPROVALS action permission it used to piggyback on.
      if (canAccessMenu(MENU_KEYS.MY_WORK)) {
        groups.push({
          label: '',
          items: [{ name: 'My Work', href: (pid: string) => programRoutes.myWork(pid), icon: Inbox, menuKey: MENU_KEYS.MY_WORK }],
        });
      }
      
      return groups;
    }

    // Program Admin (configuration role): Overview + Patient Workspace at the top
    // followed by existing configuration menus (excluding duplicates from Main group)
    if (isProgramAdminRole && !isSuperAdmin) {
      const primaryItems: NavItem[] = [];
      if (canAccessMenu(MENU_KEYS.OPERATIONAL_DASHBOARD)) {
        primaryItems.push({
          name: 'Overview',
          href: (pid: string) => programRoutes.operationalDashboard(pid),
          icon: LayoutDashboard,
          menuKey: MENU_KEYS.OPERATIONAL_DASHBOARD,
        });
      }
      primaryItems.push({
        name: 'Patient Workspace',
        href: (pid: string) => programRoutes.workspace(pid),
        icon: Users,
        menuKey: MENU_KEYS.WORKSPACE,
      });
      groups.push({ label: '', items: primaryItems });
    }

    // Duplicate menu keys to suppress from Main group (already covered by Overview/Patient Workspace)
    const suppressedMainMenuKeys: Set<string> = new Set([
      MENU_KEYS.PROGRAM_DASHBOARD,
      MENU_KEYS.WORKSPACE,
      MENU_KEYS.PHARMA_WORKSPACE,
    ]);
    
    for (const group of programStaticItems) {
      let filteredItems = group.items.filter(canAccess);
      // Remove duplicates from Main group that are already represented above
      if (group.group === 'Main') {
        filteredItems = filteredItems.filter(item => !item.menuKey || !suppressedMainMenuKeys.has(item.menuKey));
      }
      if (filteredItems.length > 0) {
        groups.push({
          label: group.group === 'Main' ? '' : group.group,
          items: filteredItems,
        });
      }
    }

    return groups;
  }, [programId, isSuperAdmin, isRoleScopedWorkspaceUser, isProgramAdminRole, workspaceKind, canAccessMenu, effectiveProgramRoleName, user?.role]);

  /* ───── Route matching ───── */

  const isActive = (href: string) => {
    if (href.endsWith('/dashboard')) return location.pathname === href;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const resolveHref = (href: string | ((pid: string) => string)): string => {
    if (typeof href === 'function' && programId) return href(programId);
    return href as string;
  };

  /* ───── Handlers ───── */

  const handleLogout = async () => {
    await logout();
    navigate(appRoutes.login(), { replace: true });
    window.location.replace(appRoutes.login());
  };

  /* ───── Render helpers ───── */

  const renderNavItem = (item: NavItem, key: string, nested = false) => {
    const href = resolveHref(item.href);
    const active = isActive(href);
    return (
      <Link
        key={key}
        to={href}
        data-tooltip={item.name}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'relative group flex items-center rounded-md transition-all duration-150',
          'text-[12px] leading-[16px]',
          nested ? 'font-normal' : 'font-medium',
          isSidebarIconMode ? 'h-9 w-9 mx-auto justify-center px-0' : nested ? 'h-9 w-full gap-2.5 pl-9 pr-3' : 'h-9 w-full gap-2.5 px-3',
          active
            ? isSidebarIconMode
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-primary text-primary-foreground font-semibold shadow-sm'
            : isSidebarIconMode
              ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <AppIcon icon={item.icon} size="button" variant="inherit" />
        {isSidebarExpanded && (
          <span className="truncate flex-1 text-[12px] leading-[16px] font-medium">
            {item.name}
          </span>
        )}
        {isSidebarExpanded && item.badge != null && item.badge > 0 && (
          <span className="ml-auto text-[11px] font-semibold bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">
            {item.badge}
          </span>
        )}
        {isSidebarExpanded && item.isWorkflowDriven && (
          <span className="ml-auto text-[11px] px-1.5 py-0.5 bg-success-soft text-success rounded font-medium leading-none">
            WF
          </span>
        )}
      </Link>
    );
  };

  const renderGroupLabel = (label: string, nested = false) => {
    if (!label || isSidebarIconMode) return null;
    return (
      <div className={cn(nested ? 'px-3 pt-3 pb-1.5' : 'px-3 mt-4 mb-2')}>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
    );
  };

  /**
   * Renders a Patient360 submenu item within the Patient Workspace group.
   * These items navigate to patient-specific routes.
   */
  const renderPatient360SubmenuItem = (
    item: WorkspaceNavItem,
    key: string,
    patient360BasePath: string,
  ) => {
    const target = item.key === 'overview' ? patient360BasePath : `${patient360BasePath}/${item.key}`;
    const active = location.pathname === target || 
      (item.key !== 'overview' && location.pathname.startsWith(`${target}/`));
    
    return (
      <Link
        key={key}
        to={target}
        data-tooltip={item.label}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'relative group flex items-center rounded-md transition-all duration-150',
          'text-[12px] leading-[16px] font-normal',
          isSidebarIconMode 
            ? 'h-9 w-9 mx-auto justify-center px-0' 
            : 'h-9 w-full gap-2.5 pl-12 pr-3',
          active
            ? isSidebarIconMode
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-primary text-primary-foreground font-medium shadow-sm'
            : isSidebarIconMode
              ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <AppIcon icon={item.icon} size="button" variant="inherit" />
        {isSidebarExpanded && (
          <span className="truncate flex-1 text-[12px] leading-[16px]">
            {item.label}
          </span>
        )}
        {isSidebarExpanded && item.badge != null && item.badge > 0 && (
          <span className="ml-auto text-[11px] font-semibold bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  /**
   * Renders the Patient Workspace navigation item with expandable Patient360 submenu.
   * When a patient is selected, the submenu automatically expands to show patient-specific items.
   */
  const renderPatientWorkspaceGroup = (keyPrefix: string) => {
    if (!programId) return null;
    
    const workspaceHref = programRoutes.workspace(programId);
    const isWorkspaceActive = location.pathname.startsWith(workspaceHref);
    const isPatient360Active = !!activePatientId;
    const isExpanded = patientWorkspaceExpanded && activePatientId;
    
    // Determine if the "Patient Workspace" header should be highlighted
    const isHeaderActive = isWorkspaceActive || isPatient360Active;
    
    // In collapsed mode, show icon only
    if (isSidebarIconMode) {
      return (
        <div>
          <Link
            to={workspaceHref}
            data-tooltip="Patient Workspace"
            aria-current={isHeaderActive ? 'page' : undefined}
            className={cn(
              'flex items-center justify-center rounded-md transition-all duration-150',
              'h-9 w-9 mx-auto',
              isHeaderActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <AppIcon icon={Users} size="button" variant="inherit" />
          </Link>
          {/* Show Patient360 submenu items in collapsed mode when patient selected */}
          {isExpanded && (
            <div className="space-y-0.5 mt-0.5">
              {patient360SubmenuItems.map((item, idx) => 
                renderPatient360SubmenuItem(
                  item, 
                  `${keyPrefix}-p360-${idx}`, 
                  getPatient360BasePath(programId, activePatientId!)
                )
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Expanded mode: show clickable header with expandable submenu
    return (
      <div className="space-y-0.5">
        {/* Patient Workspace header - always navigates to workspace */}
        <div className="flex items-center">
          <Link
            to={workspaceHref}
            data-tooltip="Patient Workspace"
            aria-current={isWorkspaceActive && !isPatient360Active ? 'page' : undefined}
            className={cn(
              'flex-1 flex items-center rounded-md transition-all duration-150',
              'h-9 gap-2.5 px-3 text-[12px] leading-[16px] font-medium',
              isHeaderActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <AppIcon icon={Users} size="button" variant="inherit" />
            <span className="truncate flex-1 text-[12px] leading-[16px] font-medium">
              Patient Workspace
            </span>
            {/* Chevron for expand/collapse when patient selected */}
            {activePatientId && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPatientWorkspaceExpanded(prev => !prev);
                }}
                className="p-0.5 -mr-1 rounded hover:bg-primary-foreground/20"
                aria-label={patientWorkspaceExpanded ? 'Collapse patient menu' : 'Expand patient menu'}
              >
                <AppIcon
                  icon={ChevronDown}
                  size="compact"
                  variant="inherit"
                  className={cn('transition-transform duration-200', patientWorkspaceExpanded && 'rotate-180')}
                />
              </button>
            )}
          </Link>
        </div>
        
        {/* Patient360 submenu - only visible when patient selected AND expanded */}
        {isExpanded && (
          <div className="space-y-0.5 ml-3 border-l border-border/70 pl-2">
            {patient360SubmenuItems.map((item, idx) => 
              renderPatient360SubmenuItem(
                item, 
                `${keyPrefix}-p360-${idx}`, 
                getPatient360BasePath(programId, activePatientId!)
              )
            )}
          </div>
        )}
      </div>
    );
  };

  /* Accordion section wrapper – collapses/expands a labelled nav section.
   * When `directPath` is provided the section has only one destination:
   * clicking the header navigates there directly (no chevron, no expand).
   * Returns null when there is nothing to show (no directPath and no content). */
  const renderAccordionSection = (
    key: 'dashboard' | 'programs' | 'admin',
    title: string,
    content: ReactNode,
    directPath?: string,
    Icon?: LucideIcon,
    headerPath?: string,
  ) => {
    // Nothing to show – hide entirely
    if (!directPath && (content === null || content === undefined)) return null;

    // Collapsed sidebar: always show the parent icon for every section.
    // The active section additionally shows its child icons indented below.
    if (isSidebarIconMode) {
      const isActiveSection = routeSection === key;
      const hasChildren = content !== null && content !== undefined;

      const parentCommonClasses = cn(
        'flex items-center justify-center rounded-md transition-all duration-150',
        'h-9 w-9 mx-auto',
        isActiveSection
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      );

      const parentIconEl = Icon ? (
          directPath ? (
            <Link to={directPath} className={parentCommonClasses} data-tooltip={title} aria-label={title}>
              <AppIcon icon={Icon} size="button" variant="inherit" />
            </Link>
          ) : headerPath ? (
            <Link to={headerPath} className={parentCommonClasses} data-tooltip={title} aria-label={title}>
              <AppIcon icon={Icon} size="button" variant="inherit" />
            </Link>
          ) : (
            <div className={parentCommonClasses} data-tooltip={title} aria-label={title}>
              <AppIcon icon={Icon} size="button" variant="inherit" />
            </div>
          )
      ) : null;

      return (
        <>
          {parentIconEl}
          {isActiveSection && hasChildren && (
            <div className="space-y-0.5 mt-0.5 mb-1">{content}</div>
          )}
        </>
      );
    }

    // Shared icon element
    const iconEl = Icon ? (
      <AppIcon icon={Icon} size="button" variant="inherit" />
    ) : null;

    // Single-child shortcut: header is a direct nav link, no accordion needed
    if (directPath) {
      const isDirectActive = isActive(directPath);
      return (
        <Link
          to={directPath}
          className={cn(
            'w-full h-9 flex items-center gap-2.5 rounded-md px-3 text-[12px] leading-[16px] font-semibold transition-all duration-150',
            isDirectActive
              ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover'
              : 'text-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {iconEl}
          <span>{title}</span>
        </Link>
      );
    }

    if (headerPath) {
      const isSectionActive = routeSection === key;

      return (
        <div>
          <div
            className={cn(
              'w-full h-9 flex items-center rounded-md px-3 text-[12px] leading-[16px] font-semibold transition-all duration-150',
              isSectionActive
                ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover'
                : 'text-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <button
              type="button"
              onClick={() => navigate(headerPath)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(headerPath);
                }
              }}
              className="flex flex-1 items-center gap-2.5 text-left"
              aria-label={`Go to ${title}`}
            >
              {iconEl}
              <span>{title}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                // Cannot collapse the currently active section
                if (routeSection === key) return;
                setManualOpen(prev => ({ ...prev, [key]: !openSections[key] }));
              }}
              className="-mr-1 p-1 rounded hover:bg-primary-foreground/20"
              aria-label={openSections[key] ? `Collapse ${title} menu` : `Expand ${title} menu`}
            >
              <AppIcon
                icon={ChevronDown}
                size="compact"
                variant="inherit"
                className={cn('transition-transform duration-200', openSections[key] && 'rotate-180')}
              />
            </button>
          </div>
          {openSections[key] && (
            <div className="space-y-0.5 mt-0.5">{content}</div>
          )}
        </div>
      );
    }

    return (
      <div>
        <button
          type="button"
          onClick={() => {
            // Cannot collapse the currently active section
            if (routeSection === key) return;
            setManualOpen(prev => ({ ...prev, [key]: !openSections[key] }));
          }}
          className={cn(
            'w-full h-9 flex items-center gap-2.5 rounded-md px-3 text-[12px] leading-[16px] font-semibold transition-all duration-150',
            routeSection === key
              ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover'
              : 'text-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {iconEl}
          <span className="flex-1 text-left">{title}</span>
          <AppIcon
            icon={ChevronDown}
            size="compact"
            variant="inherit"
            className={cn('transition-transform duration-200', openSections[key] && 'rotate-180')}
          />
        </button>
        {openSections[key] && (
          <div className="space-y-0.5 mt-0.5">{content}</div>
        )}
      </div>
    );
  };

  /** Renders nav groups from programNavGroups, skipping empty ones. Returns null if nothing to show. */
  const renderProgramNavGroups = (keyPrefix: string): ReactNode => {
    if (isLoadingMenuAccess) {
      return (
        <div className="flex items-center gap-2 px-3 py-2 text-[11px] leading-[16px] text-muted-foreground">
          <AppInlineLoader label="Loading content" />
          <span>Loading content</span>
        </div>
      );
    }
    const nonEmptyGroups = programNavGroups.filter(g => g.items.length > 0);
    if (nonEmptyGroups.length === 0) return null;
    return (
      <>
        {nonEmptyGroups.map((group, gi) => {
          // First group (no label) = Overview + Patient Workspace → render as top-level items
          // Subsequent groups (with labels) → render nested with group labels
          const isTopLevelGroup = group.label === '';
          return (
            <div key={gi}>
              {renderGroupLabel(group.label, !isTopLevelGroup)}
              {group.items.map((item, ii) => {
                // Special rendering for Patient Workspace item → expandable with Patient360 submenu
                if (item.name === 'Patient Workspace') {
                  return (
                    <React.Fragment key={`${keyPrefix}-${gi}-${ii}`}>
                      {renderPatientWorkspaceGroup(keyPrefix)}
                    </React.Fragment>
                  );
                }
                return renderNavItem(item, `${keyPrefix}-${gi}-${ii}`, !isTopLevelGroup);
              })}
            </div>
          );
        })}
      </>
    );
  };

  /* ═══════════════════════════════════════════ */
  /*  RENDER                                     */
  /* ═══════════════════════════════════════════ */

  // Workspace / dashboard routes render full-width (no content padding)
  const isFullWidthRoute =
    /\/workspace(\/|$)/.test(location.pathname) ||
    /\/operational-dashboard(\/|$)/.test(location.pathname) ||
    /\/(hospital|hcp|patient|pharma|scoped)-dashboard(\/|$)/.test(location.pathname) ||
    /\/patients\/[^/]+\/360(\/|$)/.test(location.pathname) ||
    /\/(?:hcps|patients)$/.test(location.pathname);

  // Routes where the workspace horizontal tab strip replaces the sidebar nav items.
  // Applies to all program-scoped roles (including Program Admin) on workspace/OD routes.
  // SuperAdmin retains full global navigation.
  const isWorkspaceNavTabRoute =
    !isSuperAdmin &&
    !!programId &&
    (/\/workspace(\/|$)/.test(location.pathname) ||
      /\/operational-dashboard(\/|$)/.test(location.pathname));

  // Patient360 gets its own horizontal tab strip, separate from workspace tabs.
  const isPatient360NavTabRoute =
    !isSuperAdmin &&
    !!programId &&
    /\/patients\/[^/]+\/360(\/|$)/.test(location.pathname);

  // Routes that hide the global sidebar.
  // hcp-dashboard uses WorkspaceSidebar (showWorkspaceSidebar=true), not this flag.
  // /workspace and /operational-dashboard are covered by isWorkspaceNavTabRoute for all program-scoped roles.
  // Patient360 uses its own horizontal tab strip (isPatient360NavTabRoute).
  const isSidebarHiddenRoute =
    /\/(hospital|patient|pharma|scoped)-dashboard(\/|$)/.test(location.pathname) ||
    /\/(?:hcps|patients)$/.test(location.pathname) ||
    isWorkspaceNavTabRoute ||
    isPatient360NavTabRoute;

  // Detect if this is a workspace route that needs a contextual sidebar
  const wsKind = resolveWorkspaceKind(location.pathname) as WsKind;
  const wsNavConfig = getWorkspaceNavConfig(wsKind);

  // Resolve basePath for workspace sidebar
  const wsBasePath = useMemo(() => {
    if (!wsNavConfig || !programId) return '';
    if (wsKind === 'patient360') {
      const patientId = location.pathname.match(/\/patients\/([^/]+)\/360/)?.[1];
      if (patientId) return wsNavConfig.basePath(programId, patientId);
    }
    return wsNavConfig.basePath(programId);
  }, [wsNavConfig, wsKind, programId, location.pathname]);

  const { data: pendingRegistrationApprovals } = usePatientRegistrationApprovalPendingCount(programId ?? '');
  const workspaceBadges = useMemo(() => {
    if (wsKind !== 'hcp') {
      return undefined;
    }

    return {
      'patient-registration-approvals': pendingRegistrationApprovals ?? 0,
    };
  }, [pendingRegistrationApprovals, wsKind]);

  // When a workspace sidebar is active, hide the global sidebar
  const showWorkspaceSidebar = wsNavConfig != null && !!wsBasePath;

  return (
    <PatientQuickViewProvider>
    <div className="app-shell">
      {/* ══════════  HEADER (fixed, full-width, top)  ══════════ */}
      <header className="app-header overflow-x-hidden">
        {/* Left – Logo */}
        <div data-testid="layout-header-left" className="flex flex-1 items-center gap-2 sm:gap-4 min-w-0 overflow-hidden">
          {/* Mobile hamburger — only for global sidebar (workspace pages use their own) */}
          {!isSidebarHiddenRoute && !showWorkspaceSidebar && (
            <IconGhostButton
              icon={Menu}
              tooltip="Open navigation"
              onClick={() => setMobileOpen(true)}
              className="md:hidden inline-flex min-h-0 h-10 w-10 -ml-2 rounded-lg text-muted-foreground hover:bg-surface-hover"
            />
          )}

          {/* Logo */}
          <Link
            to={isSuperAdmin ? '/superadmin/dashboard' : (programId ? roleHomePath : '/dashboard')}
            className="flex items-center shrink-0"
          >
            <ProgramBrandHeader
              programName={programName}
              programLogoUrl={programThemeSettings?.internalSidebarLogoUrl || programThemeSettings?.internalHeaderLogoUrl || null}
              defaultLogoUrl={appThemeSettings.insidePageLogoUrl || EPPREM_LOGO_URL}
              logoAltText={programThemeSettings?.logoAltText || 'EPPREM'}
              increaseFallbackLogoSize={programThemeSettings?.increaseFallbackLogoSize ?? true}
            />
          </Link>

        </div>

        {/* Right – action icons + profile */}
        <div data-testid="layout-header-right" className="flex items-center gap-1 flex-shrink-0 pl-1">
          {/* Action icons group */}
          <div className="flex items-center gap-0.5">
            {/* Explain this screen */}
            <IconGhostButton
              icon={Sparkles}
              tooltip="Explain this screen"
              onClick={openExplainPanel}
              className="min-h-0 h-8 w-8 rounded-lg text-primary hover:bg-surface-hover"
            />

            {/* Notifications (alerts engine) */}
            <AlertsBell />

            {/* Theme toggle */}
            <IconGhostButton
              icon={theme === 'dark' ? Sun : Moon}
              tooltip={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={toggleTheme}
              className={cn(
                'min-h-0 h-8 w-8 rounded-lg hover:bg-surface-hover',
                theme === 'dark' ? 'text-warning hover:text-warning' : 'text-muted-foreground'
              )}
            />
          </div>

          {/* Profile dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="hidden sm:inline-flex items-center gap-2 ml-1 min-h-0 h-9 rounded-lg px-2 hover:bg-surface-hover focus-visible:ring-0 focus-visible:ring-offset-0">
                <div className="text-right leading-tight">
                  <p className="text-[12px] font-medium text-foreground leading-snug">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    {programId && !isSuperAdmin ? (
                      isLoadingMenuAccess ? 'Loading content' : (programRoleName ?? user?.programRoleName ?? user?.role)
                    ) : (
                      user?.role
                    )}
                  </p>
                </div>
                <div className="w-7 h-7 rounded-full bg-primary-soft flex items-center justify-center flex-shrink-0">
                  <span className="text-[11px] font-semibold text-primary leading-none">
                    {(user?.firstName?.[0] || '').toUpperCase()}
                    {(user?.lastName?.[0] || '').toUpperCase()}
                  </span>
                </div>
                <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={6} className="w-56 p-0">
              {/* User info header inside dropdown */}
              <div className="px-3 py-2.5 border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-primary-soft flex items-center justify-center flex-shrink-0">
                    <span className="text-[12px] font-semibold text-primary leading-none">
                      {(user?.firstName?.[0] || '').toUpperCase()}
                      {(user?.lastName?.[0] || '').toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-medium text-foreground leading-snug truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-snug truncate" title={user?.email}>
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                {/* Theme toggle row */}
                <DropdownMenuItem
                  onClick={toggleTheme}
                  className="flex items-center gap-2.5 text-[12px] h-8 mx-1 rounded-sm cursor-pointer"
                >
                  <AppIcon icon={theme === 'dark' ? Sun : Moon} size="button" variant={theme === 'dark' ? 'warning' : 'default'} className="flex-shrink-0" />
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </DropdownMenuItem>

                {userMenuLinks.length > 0 && (
                  <>
                    <DropdownMenuSeparator className="my-1" />
                    {userMenuLinks.map((link) => (
                      <DropdownMenuItem key={link.pageKey} asChild className="h-8 mx-1 rounded-sm">
                        {link.externalUrl ? (
                          <a
                            href={link.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2.5 text-[12px]"
                          >
                            <AppIcon icon={FileText} size="button" variant="inherit" className="flex-shrink-0" />
                            {link.pageTitle}
                          </a>
                        ) : (
                          <Link
                            to={programId ? programRoutes.legalPage(programId, link.pageKey) : appRoutes.legalPage(link.pageKey)}
                            target="_blank"
                            className="flex items-center gap-2.5 text-[12px]"
                          >
                            <AppIcon icon={FileText} size="button" variant="inherit" className="flex-shrink-0" />
                            {link.pageTitle}
                          </Link>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="flex items-center gap-2.5 text-[12px] text-destructive focus:text-destructive h-8 mx-1 rounded-sm cursor-pointer"
                >
                  <AppIcon icon={LogOut} size="button" variant="inherit" className="flex-shrink-0" />
                  Logout
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* ══════════  WORKSPACE NAV TABS (role-scoped users on overview/workspace routes)  ══════════ */}
      {isWorkspaceNavTabRoute && <WorkspaceNavTabs />}

      {/* ══════════  PATIENT 360 NAV TABS (role-scoped users on patient360 routes)  ══════════ */}
      {isPatient360NavTabRoute && <Patient360NavTabs />}

      {/* ══════════  BELOW HEADER: SIDEBAR + CONTENT  ══════════ */}
      <div className="app-body">
        {/* Mobile Scrim */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* ══════════  SIDEBAR  ══════════ */}
        {showWorkspaceSidebar ? (
          /* ── Contextual workspace sidebar — replaces global admin nav ── */
          <WorkspaceSidebar
            config={wsNavConfig!}
            basePath={wsBasePath}
            backLabel="Back to Workspace"
            backPath={programId ? programRoutes.workspace(programId) : undefined}
            badges={workspaceBadges}
            programId={programId}
          />
        ) : isSidebarHiddenRoute ? null : (
        <aside
          className={cn(
            'app-sidebar',
            mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
            collapsed && 'is-collapsed',
            collapsed ? 'w-[56px] min-w-[56px]' : 'w-[240px] min-w-[240px]',
          )}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Unified collapse toggle – desktop only */}
          <button
            type="button"
            onClick={toggleSidebar}
            data-tooltip={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="sidebar-collapse-handle hidden md:inline-flex"
          >
            <span className="sidebar-collapse-grip" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>

          {/* Mobile close */}
          {mobileOpen && (
            <div className="flex items-center justify-end h-10 px-2 flex-shrink-0 md:hidden">
              <IconGhostButton
                icon={X}
                tooltip="Close navigation"
                onClick={() => setMobileOpen(false)}
                className="inline-flex min-h-0 h-9 w-9 rounded-lg text-muted-foreground hover:bg-sidebar-hover"
              />
            </div>
          )}

          {/* Navigation — overflow:visible when collapsed so CSS tooltip pseudo-elements
              are not clipped by the scrollable container */}
          <nav className={cn(
            'flex-1 min-h-0 pl-2 pr-1 space-y-1 [&::-webkit-scrollbar]:hidden',
            collapsed ? 'overflow-visible pt-1 pb-2' : 'overflow-y-auto overflow-x-hidden py-3',
          )}>
          {isSuperAdmin ? (
            /* ── SuperAdmin navigation ── */
            <>
              {/* Dashboard section – single item: navigate directly */}
              {renderAccordionSection(
                'dashboard',
                'Dashboard',
                renderNavItem(
                  { name: 'Dashboard', href: SUPERADMIN_MENU_GROUPS[0].items[0].path, icon: SUPERADMIN_MENU_GROUPS[0].items[0].icon },
                  'sa-dashboard',
                  true,
                ),
                SUPERADMIN_MENU_GROUPS[0].items[0].path,
                LayoutDashboard,
              )}

              {/* Programs section – single item when no program selected: navigate directly */}
              {renderAccordionSection(
                'programs',
                programId ? 'Program' : 'Programs',
                programId ? renderProgramNavGroups('pg') : null,
                !programId ? '/programs' : undefined,
                FolderKanban,
                programId ? appRoutes.programs() : undefined,
              )}

              {/* Admin section */}
              {renderAccordionSection(
                'admin',
                'Admin',
                <>
                  {SUPERADMIN_MENU_GROUPS.filter(g => g.key !== 'overview').map(group => (
                    <div key={group.key}>
                      {renderGroupLabel(group.label, true)}
                      {group.items.map((item, index) =>
                        renderNavItem(
                          { name: item.label, href: item.path, icon: item.icon },
                          `${group.key}-${index}`,
                          true,
                        ),
                      )}
                    </div>
                  ))}
                </>,
                undefined,
                Shield,
              )}
            </>
          ) : (
            /* ── Program user navigation — flat layout (no accordion wrapper) ── */
            programId ? (
              renderProgramNavGroups('pu')
            ) : (
              /* No program selected */
                <Link
                  to={appRoutes.programSelection()}
                  data-tooltip="Programs"
                  className={cn(
                    'flex items-center rounded-md text-[12px] leading-[16px] font-medium transition-all duration-150',
                    isSidebarIconMode ? 'h-9 w-9 mx-auto justify-center px-0' : 'h-9 w-full gap-2.5 px-3',
                    'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <AppIcon icon={FolderKanban} size="button" variant="inherit" />
                  {isSidebarExpanded && <span className="truncate text-[12px] leading-[16px] font-medium">Select Program</span>}
                </Link>
            )
          )}
        </nav>

        {/* Sidebar Footer */}
        <SidebarFooter
          programId={programId}
          collapsed={isSidebarIconMode}
        />
      </aside>
        )}

        {/* ══════════  CONTENT AREA (only scrollable region)  ══════════ */}
        <main className={cn('app-content', isFullWidthRoute && 'app-content--full-width')}>
          <div className={isFullWidthRoute ? 'h-full' : 'px-4 sm:px-5 lg:px-6 py-4 space-y-4'}>
            {!isFullWidthRoute && resolvedPageInstruction ? (
              <PageInstructionBanner
                pageRouteKey={resolvedPageInstruction.pageRouteKey}
                programId={resolvedPageInstruction.programId}
              />
            ) : null}
            <PageInstructionRenderProvider value={resolvedPageInstruction}>
              {children ?? <Outlet />}
            </PageInstructionRenderProvider>
          </div>
        </main>
      </div>
    </div>
    <PatientQuickViewDrawer programId={programId} />
    </PatientQuickViewProvider>
  );
}

/**
 * PatientQuickViewDrawer – Renders the global patient drawer
 */
function PatientQuickViewDrawer({ programId }: { programId: string | undefined }) {
  const { state, closePatientQuickView } = usePatientQuickView();
  const effectiveProgramId = state.programId || programId || '';
  
  return (
    <PatientQuickView
      isOpen={state.isOpen}
      onClose={closePatientQuickView}
      patientId={state.patientId}
      programId={effectiveProgramId}
    />
  );
}
